<template>
  <div
    class="flex items-start bg-neutral-secondary w-full flex-col rounded-none py-1"
  >
    <div
      class="cursor-pointer rounded-full flex items-center justify-center bg-neutral-primary pl-3 pr-2.5 h-6 hover:bg-transparent mb-2"
      @click.stop="emit('handlePortfolioVisualization')"
    >
      <NegarShrinkIcon size="16" class="ml-1 text-neutral-primary" />

      <NText variant="overline" class="text-neutral-primary" weight="medium">
        {{ t('misc.close') }}
      </NText>
    </div>

    <NTabtables
      v-model="tab"
      auto-activate
      class="w-full !h-[calc(100%-75px)] !p-0"
    >
      <NTabtable
        v-gid="{
          module: 'portfolio',
          screen: 'home',
          section: 'portfolioVisualizationTabs',
          component: 'portfolioTabButton',
        }"
        :title="t('modules.portfolio.title')"
        @click="handlePortfolioVisualizationClick"
      >
        <PortfolioVisualizationTab :portfolio-symbols="portfolioSymbols" />
      </NTabtable>

      <NTabtable
        v-gid="{
          module: 'portfolio',
          screen: 'home',
          section: 'portfolioVisualizationTabs',
          component: 'virtualPortfolioTabButton',
        }"
        :title="t('modules.portfolio.virtualPortfolio.title')"
        @click="handleVirtualPortfolioVisualizationClick"
      >
        <VirtualPortfolioVisualizationTab
          :virtual-portfolio-symbols="virtualPortfolioSymbols"
          @openCreateVirtualPortfolioItemDialog="
            openCreateVirtualPortfolioItemDialog
          "
        />
      </NTabtable>
    </NTabtables>
    <PortfolioSummary
      :portfolio-summary="
        tab === PORTFOLIO_VISUALIZATION_TAB.portfolio
          ? portfolioSummary
          : virtualPortfolioSummary
      "
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { NegarShrinkIcon } from '@negar/icons';
import { PortfolioItem } from '@libs/api-reference';
import { useRoute, useRouter } from 'vue-router';

import {
  PortfolioSummary,
  PortfolioVisualizationTab,
  VirtualPortfolioVisualizationTab,
  usePortfolioDialogs,
} from '@/modules/portfolio';
import { PORTFOLIO_VISUALIZATION_TAB } from '../../constants';
import {
  useLivePortfolioQuery,
  useLiveVirtualPortfolioQuery,
} from '@core/portfolio/src';

const emit = defineEmits<{
  (e?: 'handlePortfolioVisualization'): void;
}>();

defineProps<{
  portfolioSymbols: PortfolioItem[];
}>();

const route = useRoute();
const router = useRouter();

const tab = ref(
  route.query.virtualPortfolio
    ? PORTFOLIO_VISUALIZATION_TAB.virtualPortfolio
    : PORTFOLIO_VISUALIZATION_TAB.portfolio
);

const { t } = useI18n();

const handlePortfolioVisualizationClick = () => {
  const newQuery = { ...route.query };

  delete newQuery.virtualPortfolio;

  router.push({ query: { ...newQuery, portfolio: 'true' } });
};

const { virtualPortfolioStats, symbols: virtualPortfolioSymbols } =
  useLiveVirtualPortfolioQuery({
    immediate: computed(() => Boolean(route.query.virtualPortfolio)),
  });

const { openCreateVirtualPortfolioItemDialog } = usePortfolioDialogs();

const virtualPortfolioSummary = computed(() => ({
  ...virtualPortfolioStats.value,
  quantity: virtualPortfolioSymbols.value.length,
}));

const { stats: portfolioStats, symbols: portfolioSymbols } =
  useLivePortfolioQuery();
const portfolioSummary = computed(() => ({
  ...portfolioStats.value,
  quantity: portfolioSymbols.value.length,
}));

const handleVirtualPortfolioVisualizationClick = () => {
  const newQuery = { ...route.query };

  delete newQuery.portfolio;

  router.push({ query: { ...newQuery, virtualPortfolio: 'true' } });
};
</script>
